import {
  Component,
  ElementRef,
  ViewChild,
  inject,
  signal,
  computed,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { LucideAngularModule } from 'lucide-angular';
import { ShortcutService } from '../../../core/services/shortcut.service';
import { CommandInterface } from '../../../core/interfaces/command.interface';

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, DialogModule, InputTextModule, FormsModule, LucideAngularModule],
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss']
})
export class CommandPaletteComponent {
  private readonly _shortcutService = inject(ShortcutService);

  searchQuery = signal('');
  readonly commands = this._shortcutService.commands;
  selectedIndex = signal(0);

  get visible(): boolean {
    return this._shortcutService.visible();
  }

  set visible(value: boolean) {
    if (!value) {
      this._shortcutService.close();
    } else {
      this._shortcutService.open();
    }
  }

  // Computed filtered commands
  filteredCommands = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.commands();
    }

    return this.commands().filter((cmd) => {
      const matchesLabel = cmd.label.toLowerCase().includes(query);
      const matchesCategory = cmd.category.toLowerCase().includes(query);
      const matchesKeywords = cmd.keywords?.some((k) => k.toLowerCase().includes(query));

      return matchesLabel || matchesCategory || matchesKeywords;
    });
  });

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  constructor() {
    effect(() => {
      const isVisible = this._shortcutService.visible();
      if (isVisible) {
        this.resetState();
        setTimeout(() => this.focusInput(), 50);
      }
    });
  }

  private resetState() {
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  private focusInput() {
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }

  onHide() {
    this._shortcutService.close();
  }

  onKeyDown(event: KeyboardEvent) {
    const list = this.filteredCommands();
    if (!list.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex.update((i) => (i + 1) % list.length);
      this.scrollToSelected();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex.update((i) => (i - 1 + list.length) % list.length);
      this.scrollToSelected();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.selectCommand(list[this.selectedIndex()]);
    }
  }

  selectCommand(command: CommandInterface) {
    this._shortcutService.executeCommand(command);
  }

  // Scroll logic to keep the selected item in view
  private scrollToSelected() {
    // Implementation would require referencing the list container
    // and calculating scroll position.
    // For MVP we rely on simple visible scrolling or CSS scroll-snap if possible.
    // We can try to find the element by ID or class.
    const el = document.getElementById(`cmd-item-${this.selectedIndex()}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }
}
